<template>
  <div class="collab-page">
    <!-- 顶部协同统计 -->
    <div class="collab-header">
      <div v-for="(item, idx) in headerStats" :key="idx" class="h-stat">
        <div class="h-icon" :style="{ background: item.bg, color: item.color }">{{ item.icon }}</div>
        <div class="h-info">
          <div class="h-value" :style="{ color: item.color }">{{ item.value }}</div>
          <div class="h-label">{{ item.label }}</div>
        </div>
      </div>
    </div>

    <!-- 主体三栏：消息 | 协同区 | 侧边信息 -->
    <div class="collab-main">
      <!-- 左侧：消息列表 -->
      <div class="msg-panel">
        <div class="panel-header">
          <span class="panel-title">◈ 消息中心</span>
          <div class="msg-tabs">
            <span class="tab active">全部</span>
            <span class="tab">未读 3</span>
          </div>
        </div>
        <div class="msg-search">
          <input type="text" placeholder="搜索供应商或消息..." v-model="searchKey">
        </div>
        <div class="msg-list scroll-area">
          <div v-for="(msg, idx) in filteredMsgs" :key="idx" class="msg-item" :class="{ active: currentMsg === idx, unread: msg.unread }" @click="currentMsg = idx">
            <div class="msg-avatar" :style="{ background: msg.avatarColor }">{{ msg.avatar }}</div>
            <div class="msg-body">
              <div class="msg-top">
                <span class="msg-name">{{ msg.name }}</span>
                <span class="msg-time">{{ msg.time }}</span>
              </div>
              <div class="msg-preview">{{ msg.preview }}</div>
              <div v-if="msg.unread" class="msg-badge">{{ msg.unread }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 中间：协同工作区 -->
      <div class="work-panel">
        <div class="chat-header">
          <div class="chat-target">
            <span class="target-avatar" :style="{ background: currentMsgData.avatarColor }">{{ currentMsgData.avatar }}</span>
            <div class="target-info">
              <div class="target-name">{{ currentMsgData.name }}</div>
              <div class="target-status">
                <span class="status-dot online"></span>
                在线
              </div>
            </div>
          </div>
          <div class="chat-tools">
            <button class="tool-btn" @click="handleCall">📞</button>
            <button class="tool-btn" @click="handleShareFile">📎</button>
            <button class="tool-btn" @click="handleMore">⋮</button>
          </div>
        </div>
        <div class="chat-body scroll-area">
          <div v-for="(chat, idx) in chatHistory" :key="idx" class="chat-bubble" :class="{ self: chat.isSelf }">
            <div class="bubble-avatar" v-if="!chat.isSelf" :style="{ background: chat.avatarColor }">{{ chat.avatar }}</div>
            <div class="bubble-content">
              <div class="bubble-name" v-if="!chat.isSelf">{{ chat.name }}</div>
              <div class="bubble-text" :class="{ file: chat.type === 'file', image: chat.type === 'image' }">
                <template v-if="chat.type === 'text'">{{ chat.content }}</template>
                <template v-if="chat.type === 'file'">
                  <div class="file-card">
                    <span class="file-icon">📄</span>
                    <div class="file-info">
                      <div class="file-name">{{ chat.fileName }}</div>
                      <div class="file-size">{{ chat.fileSize }}</div>
                    </div>
                    <button class="file-download" @click="handleDownload(chat)">↓</button>
                  </div>
                </template>
                <template v-if="chat.type === 'image'">
                  <div class="image-card" @click="handlePreview(chat)">
                    <div class="image-placeholder">{{ chat.content }}</div>
                  </div>
                </template>
              </div>
              <div class="bubble-time">{{ chat.time }}</div>
            </div>
          </div>
        </div>
        <div class="chat-input">
          <div class="input-tools">
            <button class="input-tool">😊</button>
            <button class="input-tool" @click="handleUpload">📎</button>
            <button class="input-tool">📷</button>
          </div>
          <textarea v-model="inputMsg" placeholder="输入消息，按 Enter 发送..." @keydown.enter.prevent="sendMsg"></textarea>
          <button class="send-btn" @click="sendMsg">发送</button>
        </div>
      </div>

      <!-- 右侧：在线成员 + 共享文件 -->
      <div class="side-panel">
        <div class="side-block">
          <div class="side-title">◉ 在线成员</div>
          <div class="member-list">
            <div v-for="(m, idx) in onlineMembers" :key="idx" class="member-item">
              <div class="member-avatar" :style="{ background: m.color }">{{ m.avatar }}</div>
              <div class="member-info">
                <div class="member-name">{{ m.name }}</div>
                <div class="member-role">{{ m.role }}</div>
              </div>
              <span class="member-status" :class="m.status"></span>
            </div>
          </div>
        </div>
        <div class="side-block">
          <div class="side-title">▣ 共享文件</div>
          <div class="file-list">
            <div v-for="(f, idx) in sharedFiles" :key="idx" class="file-item">
              <span class="file-type-icon" :class="f.type">{{ fileIcon(f.type) }}</span>
              <div class="file-info">
                <div class="file-name">{{ f.name }}</div>
                <div class="file-meta">{{ f.size }} · {{ f.time }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="side-block">
          <div class="side-title">◆ 协同任务</div>
          <div class="task-mini">
            <div v-for="(t, idx) in miniTasks" :key="idx" class="task-item" :class="{ done: t.done }">
              <input type="checkbox" v-model="t.done">
              <span class="task-text">{{ t.text }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部：协同任务看板 -->
    <div class="task-board">
      <div class="board-header">
        <span class="board-title">◊ 协同任务看板</span>
        <button class="action-btn" @click="handleNewTask">新建任务</button>
      </div>
      <div class="board-columns">
        <div v-for="(col, idx) in taskColumns" :key="idx" class="board-col">
          <div class="col-header">
            <span class="col-dot" :style="{ background: col.color }"></span>
            <span class="col-name">{{ col.name }}</span>
            <span class="col-count">{{ col.tasks.length }}</span>
          </div>
          <div class="col-body scroll-area">
            <div v-for="(task, tidx) in col.tasks" :key="tidx" class="task-card" :class="task.priority">
              <div class="task-tag" :style="{ background: col.color + '20', color: col.color }">{{ task.tag }}</div>
              <div class="task-title">{{ task.title }}</div>
              <div class="task-desc">{{ task.desc }}</div>
              <div class="task-footer">
                <div class="task-members">
                  <span v-for="(mem, midx) in task.members" :key="midx" class="task-mem" :style="{ background: mem.color }">{{ mem.name }}</span>
                </div>
                <span class="task-deadline" :class="{ urgent: task.isUrgent }">{{ task.deadline }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SrmCollaboration',
  data() {
    return {
      headerStats: [
        { icon: '◈', value: '12', label: '在线协同', bg: 'rgba(0, 212, 255, 0.1)', color: '#00d4ff' },
        { icon: '◉', value: '86', label: '今日消息', bg: 'rgba(0, 229, 160, 0.1)', color: '#00e5a0' },
        { icon: '▣', value: '24', label: '共享文件', bg: 'rgba(240, 160, 48, 0.1)', color: '#f0a030' },
        { icon: '◆', value: '18', label: '待办任务', bg: 'rgba(255, 107, 107, 0.1)', color: '#ff6b6b' },
        { icon: '◊', value: '6', label: '进行中项目', bg: 'rgba(168, 85, 247, 0.1)', color: '#a855f7' }
      ],
      searchKey: '',
      currentMsg: 0,
      inputMsg: '',
      msgList: [
        { name: '深圳华强电子', avatar: '华', avatarColor: '#00d4ff', preview: '报价单已更新，请查收附件', time: '11:35', unread: 2 },
        { name: '苏州精密制造', avatar: '苏', avatarColor: '#00e5a0', preview: '样品已寄出，顺丰单号SF123456', time: '11:20', unread: 0 },
        { name: '杭州半导体', avatar: '杭', avatarColor: '#f0a030', preview: '技术规格书确认完毕', time: '10:45', unread: 1 },
        { name: '青岛海润化工', avatar: '青', avatarColor: '#ff6b9d', preview: '下月交货计划已发送', time: '10:10', unread: 0 },
        { name: '北京自动化设备', avatar: '北', avatarColor: '#a855f7', preview: '合同条款第3条需要协商', time: '09:30', unread: 0 },
        { name: '武汉光谷光电', avatar: '武', avatarColor: '#ff6b6b', preview: '交付延期说明及补救方案', time: '昨天', unread: 0 },
        { name: '东莞金属制品', avatar: '莞', avatarColor: '#8c9bb3', preview: '质检报告已上传', time: '昨天', unread: 0 }
      ],
      chatHistory: [
        { name: '深圳华强电子', avatar: '华', avatarColor: '#00d4ff', content: '您好，关于上次的采购询价，我们更新了报价单', time: '11:20', isSelf: false, type: 'text' },
        { name: '我', avatar: '我', avatarColor: '#00d4ff', content: '收到，我这边看一下', time: '11:22', isSelf: true, type: 'text' },
        { name: '深圳华强电子', avatar: '华', avatarColor: '#00d4ff', fileName: '2026Q3报价单_华强电子.xlsx', fileSize: '2.4 MB', time: '11:25', isSelf: false, type: 'file' },
        { name: '我', avatar: '我', avatarColor: '#00d4ff', content: '价格相比Q2有下调吗？', time: '11:28', isSelf: true, type: 'text' },
        { name: '深圳华强电子', avatar: '华', avatarColor: '#00d4ff', content: '是的，批量采购1000pcs以上可以享受8%的折扣', time: '11:30', isSelf: false, type: 'text' },
        { name: '深圳华强电子', avatar: '华', avatarColor: '#00d4ff', content: '[产品规格图纸]', time: '11:32', isSelf: false, type: 'image' },
        { name: '我', avatar: '我', avatarColor: '#00d4ff', content: '好的，我和内部确认后回复您', time: '11:35', isSelf: true, type: 'text' }
      ],
      onlineMembers: [
        { name: '张经理', avatar: '张', role: '采购主管', color: '#00d4ff', status: 'online' },
        { name: '李工', avatar: '李', role: '技术审核', color: '#00e5a0', status: 'online' },
        { name: '王财务', avatar: '王', role: '财务审批', color: '#f0a030', status: 'away' },
        { name: '赵法务', avatar: '赵', role: '法务审核', color: '#ff6b9d', status: 'online' },
        { name: '刘质量', avatar: '刘', role: '质量管控', color: '#a855f7', status: 'offline' }
      ],
      sharedFiles: [
        { name: '2026年度采购计划.pdf', size: '3.2 MB', time: '2小时前', type: 'pdf' },
        { name: '供应商评估标准.xlsx', size: '1.8 MB', time: '昨天', type: 'excel' },
        { name: '技术规格书_V2.3.docx', size: '5.6 MB', time: '昨天', type: 'word' },
        { name: '产品图纸_2026Q3.zip', size: '128 MB', time: '3天前', type: 'zip' }
      ],
      miniTasks: [
        { text: '确认华强电子Q3报价', done: false },
        { text: '审核精密制造样品', done: true },
        { text: '跟进海润化工交期', done: false },
        { text: '签署半导体技术协议', done: false }
      ],
      taskColumns: [
        {
          name: '待处理',
          color: '#f0a030',
          tasks: [
            { title: '确认Q3电子元器件报价', desc: '华强电子已更新报价单，需内部审批', tag: '询价', priority: 'normal', members: [{ name: '张', color: '#00d4ff' }], deadline: '6月26日', isUrgent: false },
            { title: '审核精密制造样品报告', desc: '样品检测报告已收到，需质量确认', tag: '质检', priority: 'high', members: [{ name: '刘', color: '#a855f7' }], deadline: '6月25日', isUrgent: true }
          ]
        },
        {
          name: '进行中',
          color: '#00d4ff',
          tasks: [
            { title: '半导体技术协议谈判', desc: '与杭州半导体就技术条款进行协商', tag: '合同', priority: 'high', members: [{ name: '张', color: '#00d4ff' }, { name: '赵', color: '#ff6b9d' }], deadline: '6月28日', isUrgent: false },
            { title: '化工原料供应商现场考察', desc: '安排对青岛海润化工的实地考察', tag: '考察', priority: 'normal', members: [{ name: '李', color: '#00e5a0' }], deadline: '7月2日', isUrgent: false }
          ]
        },
        {
          name: '已完成',
          color: '#00e5a0',
          tasks: [
            { title: '金属材料供应商准入审批', desc: '东莞金属制品资质审核已通过', tag: '准入', priority: 'normal', members: [{ name: '张', color: '#00d4ff' }, { name: '刘', color: '#a855f7' }], deadline: '6月20日', isUrgent: false },
            { title: 'Q2供应商绩效评估', desc: '季度绩效评估报告已归档', tag: '绩效', priority: 'normal', members: [{ name: '张', color: '#00d4ff' }], deadline: '6月15日', isUrgent: false }
          ]
        },
        {
          name: '已归档',
          color: '#8c9bb3',
          tasks: [
            { title: '2025年度供应商大会', desc: '年度供应商大会资料归档', tag: '会议', priority: 'low', members: [{ name: '张', color: '#00d4ff' }], deadline: '5月30日', isUrgent: false }
          ]
        }
      ]
    }
  },
  computed: {
    filteredMsgs() {
      if (!this.searchKey) return this.msgList
      return this.msgList.filter(m => m.name.includes(this.searchKey) || m.preview.includes(this.searchKey))
    },
    currentMsgData() {
      return this.msgList[this.currentMsg] || this.msgList[0]
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize() {},
    sendMsg() {
      if (!this.inputMsg.trim()) return
      this.chatHistory.push({
        name: '我',
        avatar: '我',
        avatarColor: '#00d4ff',
        content: this.inputMsg,
        time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }),
        isSelf: true,
        type: 'text'
      })
      this.inputMsg = ''
      this.$nextTick(() => {
        const el = this.$el.querySelector('.chat-body')
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    handleCall() {
      alert('发起语音通话...')
    },
    handleShareFile() {
      alert('选择文件分享...')
    },
    handleMore() {
      alert('更多操作...')
    },
    handleUpload() {
      alert('上传文件...')
    },
    handleDownload(chat) {
      alert(`下载文件：${chat.fileName}`)
    },
    handlePreview(chat) {
      alert(`预览图片：${chat.content}`)
    },
    handleNewTask() {
      alert('新建协同任务')
    },
    fileIcon(type) {
      const map = { pdf: '📕', excel: '📗', word: '📘', zip: '📦' }
      return map[type] || '📄'
    }
  }
}
</script>

<style lang="scss" scoped>
.collab-page {
  width: 100%;
  min-height: 100%;
  padding-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ==================== 顶部统计 ==================== */
.collab-header {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
}

.h-stat {
  background: rgba(0, 212, 255, 0.04);
  border: 1px solid rgba(0, 212, 255, 0.1);
  border-radius: 10px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.3s;

  &:hover {
    background: rgba(0, 212, 255, 0.07);
    transform: translateY(-2px);
  }
}

.h-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.h-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.h-value {
  font-size: 22px;
  font-weight: bold;
}

.h-label {
  font-size: 12px;
  color: #8c9bb3;
}

/* ==================== 主体三栏 ==================== */
.collab-main {
  display: grid;
  grid-template-columns: 260px 1fr 240px;
  gap: 16px;
  height: 520px;
}

.msg-panel, .work-panel, .side-panel {
  background: rgba(0, 212, 255, 0.03);
  border: 1px solid rgba(0, 212, 255, 0.1);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ==================== 左侧面板 ==================== */
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.08);
  flex-shrink: 0;
}

.panel-title {
  font-size: 14px;
  color: #fff;
  font-weight: bold;
  letter-spacing: 1px;
}

.msg-tabs {
  display: flex;
  gap: 8px;
}

.tab {
  font-size: 12px;
  color: #8c9bb3;
  cursor: pointer;
  padding: 2px 8px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover, &.active {
    background: rgba(0, 212, 255, 0.1);
    color: #00d4ff;
  }
}

.msg-search {
  padding: 10px 16px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.05);

  input {
    width: 100%;
    padding: 8px 12px;
    border-radius: 6px;
    border: 1px solid rgba(0, 212, 255, 0.15);
    background: rgba(0, 212, 255, 0.05);
    color: #fff;
    font-size: 12px;
    outline: none;

    &::placeholder {
      color: #8c9bb3;
    }

    &:focus {
      border-color: rgba(0, 212, 255, 0.4);
    }
  }
}

.msg-list {
  flex: 1;
  overflow-y: auto;
}

.msg-item {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;

  &:hover {
    background: rgba(0, 212, 255, 0.05);
  }

  &.active {
    background: rgba(0, 212, 255, 0.1);
    border-left: 2px solid #00d4ff;
  }

  &.unread {
    .msg-preview {
      color: #fff;
      font-weight: bold;
    }
  }
}

.msg-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #fff;
  flex-shrink: 0;
}

.msg-body {
  flex: 1;
  min-width: 0;
  position: relative;
}

.msg-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.msg-name {
  font-size: 13px;
  color: #fff;
}

.msg-time {
  font-size: 11px;
  color: #8c9bb3;
}

.msg-preview {
  font-size: 12px;
  color: #8c9bb3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 20px;
}

.msg-badge {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ff6b6b;
  color: #fff;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ==================== 中间工作区 ==================== */
.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.08);
  flex-shrink: 0;
}

.chat-target {
  display: flex;
  align-items: center;
  gap: 10px;
}

.target-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #fff;
}

.target-name {
  font-size: 14px;
  color: #fff;
  font-weight: bold;
}

.target-status {
  font-size: 12px;
  color: #8c9bb3;
  display: flex;
  align-items: center;
  gap: 4px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;

  &.online {
    background: #00e5a0;
    box-shadow: 0 0 6px rgba(0, 229, 160, 0.5);
  }
}

.chat-tools {
  display: flex;
  gap: 8px;
}

.tool-btn {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid rgba(0, 212, 255, 0.2);
  background: transparent;
  color: #8c9bb3;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    border-color: #00d4ff;
    color: #00d4ff;
    background: rgba(0, 212, 255, 0.08);
  }
}

.chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.chat-bubble {
  display: flex;
  gap: 10px;
  max-width: 80%;

  &.self {
    align-self: flex-end;
    flex-direction: row-reverse;

    .bubble-content {
      align-items: flex-end;
    }

    .bubble-text {
      background: rgba(0, 212, 255, 0.15);
      border-color: rgba(0, 212, 255, 0.3);
    }
  }
}

.bubble-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #fff;
  flex-shrink: 0;
}

.bubble-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.bubble-name {
  font-size: 11px;
  color: #8c9bb3;
}

.bubble-text {
  background: rgba(0, 212, 255, 0.08);
  border: 1px solid rgba(0, 212, 255, 0.15);
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13px;
  color: #c8d4e8;
  line-height: 1.5;

  &.file {
    padding: 0;
    overflow: hidden;
  }

  &.image {
    padding: 0;
    background: transparent;
    border: none;
  }
}

.file-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: rgba(0, 212, 255, 0.05);
}

.file-icon {
  font-size: 24px;
}

.file-info {
  flex: 1;
  min-width: 0;
}

.file-name {
  font-size: 12px;
  color: #fff;
}

.file-size {
  font-size: 11px;
  color: #8c9bb3;
}

.file-download {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  border: 1px solid rgba(0, 212, 255, 0.3);
  background: rgba(0, 212, 255, 0.1);
  color: #00d4ff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;

  &:hover {
    background: rgba(0, 212, 255, 0.2);
  }
}

.image-card {
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
}

.image-placeholder {
  width: 200px;
  height: 120px;
  background: rgba(0, 212, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00d4ff;
  font-size: 13px;
  border: 1px dashed rgba(0, 212, 255, 0.3);
}

.bubble-time {
  font-size: 11px;
  color: #8c9bb3;
}

.chat-input {
  padding: 12px 16px;
  border-top: 1px solid rgba(0, 212, 255, 0.08);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-tools {
  display: flex;
  gap: 8px;
}

.input-tool {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  border: none;
  background: transparent;
  color: #8c9bb3;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    color: #00d4ff;
    background: rgba(0, 212, 255, 0.08);
  }
}

textarea {
  width: 100%;
  height: 60px;
  border-radius: 8px;
  border: 1px solid rgba(0, 212, 255, 0.15);
  background: rgba(0, 212, 255, 0.05);
  color: #fff;
  font-size: 13px;
  padding: 10px 14px;
  resize: none;
  outline: none;
  font-family: inherit;

  &::placeholder {
    color: #8c9bb3;
  }

  &:focus {
    border-color: rgba(0, 212, 255, 0.4);
  }
}

.send-btn {
  align-self: flex-end;
  padding: 8px 20px;
  border-radius: 6px;
  border: none;
  background: rgba(0, 212, 255, 0.2);
  color: #00d4ff;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 212, 255, 0.3);
    box-shadow: 0 0 12px rgba(0, 212, 255, 0.2);
  }
}

/* ==================== 右侧面板 ==================== */
.side-panel {
  gap: 0;
}

.side-block {
  padding: 14px 16px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }
}

.side-title {
  font-size: 13px;
  color: #fff;
  font-weight: bold;
  margin-bottom: 12px;
  letter-spacing: 1px;
}

.member-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.member-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.member-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #fff;
  flex-shrink: 0;
}

.member-info {
  flex: 1;
  min-width: 0;
}

.member-name {
  font-size: 13px;
  color: #fff;
}

.member-role {
  font-size: 11px;
  color: #8c9bb3;
}

.member-status {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;

  &.online {
    background: #00e5a0;
    box-shadow: 0 0 6px rgba(0, 229, 160, 0.4);
  }

  &.away {
    background: #f0a030;
  }

  &.offline {
    background: rgba(140, 155, 179, 0.3);
  }
}

.file-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.file-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  border-radius: 6px;
  background: rgba(0, 212, 255, 0.03);
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 212, 255, 0.08);
  }
}

.file-type-icon {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;

  &.pdf { background: rgba(255, 107, 107, 0.1); }
  &.excel { background: rgba(0, 229, 160, 0.1); }
  &.word { background: rgba(0, 212, 255, 0.1); }
  &.zip { background: rgba(240, 160, 48, 0.1); }
}

.task-mini {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.task-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #c8d4e8;

  &.done {
    .task-text {
      text-decoration: line-through;
      color: #8c9bb3;
    }
  }

  input[type="checkbox"] {
    accent-color: #00d4ff;
  }
}

/* ==================== 底部任务看板 ==================== */
.task-board {
  background: rgba(0, 212, 255, 0.03);
  border: 1px solid rgba(0, 212, 255, 0.1);
  border-radius: 10px;
  padding: 16px;
}

.board-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.board-title {
  font-size: 14px;
  color: #fff;
  font-weight: bold;
  letter-spacing: 1px;
}

.action-btn {
  padding: 6px 14px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  border: 1px solid rgba(0, 212, 255, 0.4);
  background: rgba(0, 212, 255, 0.08);
  color: #00d4ff;
  transition: all 0.3s;

  &:hover {
    background: rgba(0, 212, 255, 0.15);
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.2);
  }
}

.board-columns {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.board-col {
  background: rgba(0, 212, 255, 0.03);
  border: 1px solid rgba(0, 212, 255, 0.06);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  max-height: 320px;
}

.col-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.06);
}

.col-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.col-name {
  font-size: 13px;
  color: #fff;
  font-weight: bold;
}

.col-count {
  margin-left: auto;
  font-size: 12px;
  color: #8c9bb3;
  background: rgba(0, 212, 255, 0.08);
  padding: 2px 8px;
  border-radius: 10px;
}

.col-body {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.task-card {
  background: rgba(0, 212, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.1);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 10px;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(0, 212, 255, 0.2);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &.high {
    border-left: 3px solid #ff6b6b;
  }

  &.normal {
    border-left: 3px solid #00d4ff;
  }
}

.task-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  margin-bottom: 8px;
}

.task-title {
  font-size: 13px;
  color: #fff;
  font-weight: bold;
  margin-bottom: 6px;
}

.task-desc {
  font-size: 12px;
  color: #8c9bb3;
  margin-bottom: 12px;
  line-height: 1.4;
}

.task-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.task-members {
  display: flex;
  gap: -4px;
}

.task-mem {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: #fff;
  border: 2px solid #011039;
  margin-left: -6px;

  &:first-child {
    margin-left: 0;
  }
}

.task-deadline {
  font-size: 11px;
  color: #8c9bb3;

  &.urgent {
    color: #ff6b6b;
    font-weight: bold;
  }
}

/* ==================== 滚动条 ==================== */
.scroll-area {
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(0, 212, 255, 0.05);
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 212, 255, 0.2);
    border-radius: 2px;
  }
}

/* ==================== 响应式 ==================== */
@media (max-width: 1400px) {
  .collab-header {
    grid-template-columns: repeat(3, 1fr);
  }

  .collab-main {
    grid-template-columns: 1fr;
    height: auto;
  }

  .msg-panel, .side-panel {
    height: 400px;
  }

  .board-columns {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .collab-header {
    grid-template-columns: repeat(2, 1fr);
  }

  .board-columns {
    grid-template-columns: 1fr;
  }
}
</style>